import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core"
import { IconChevronDown, IconChevronUp, IconSearch, IconSelector, IconX } from "@tabler/icons-react"
import { useCallback, useMemo, useState } from "react"
import { formatDateTime, formatInteger, formatRelative, parsePgTimestamp } from "../../../../lib/format-date"
import { buildRows, compareRows, defaultSortDir } from "./helpers"
import type { IDictionaryRow, IProps, TSortDir, TSortKey } from "./interfaces"
import classes from "./styles.module.css"

/** Placeholder cell widths for the loading skeleton. */
const SKELETON_WIDTHS = [72, 104, 64, 88, 76, 96]
/** How many rows to show before the "show more" control kicks in. */
const INITIAL_VISIBLE = 40
/** How many more rows each "show more" click reveals. */
const PAGE = 40

interface ISortHeaderProps {
  label: React.ReactNode
  sortBy: TSortKey
  active: boolean
  dir: TSortDir
  numeric?: boolean
  onSort: (key: TSortKey) => void
}

function SortHeader({ label, sortBy, active, dir, numeric = false, onSort }: ISortHeaderProps): React.JSX.Element {
  const icon = !active ? (
    <IconSelector size={14} className={classes.sortIcon} aria-hidden />
  ) : dir === "asc" ? (
    <IconChevronUp size={14} className={classes.sortIcon} aria-hidden />
  ) : (
    <IconChevronDown size={14} className={classes.sortIcon} aria-hidden />
  )
  return (
    <Table.Th className={numeric ? classes.numericTh : undefined}>
      <UnstyledButton className={classes.sortButton} data-numeric={numeric} onClick={() => onSort(sortBy)}>
        <span>{label}</span>
        {icon}
      </UnstyledButton>
    </Table.Th>
  )
}

function RelativeCell({ iso, now }: { iso: string | null; now: number }): React.JSX.Element {
  const { i18n } = useLingui()
  const then = parsePgTimestamp(iso)
  if (!then) {
    return (
      <Text size="sm" c="dimmed">
        <Trans>Never</Trans>
      </Text>
    )
  }
  return (
    <Tooltip label={formatDateTime(i18n, then)} withArrow>
      <Text size="sm">{formatRelative(i18n, now - then.getTime(), { dayGranular: true })}</Text>
    </Tooltip>
  )
}

function NumericCell({ value }: { value: number | null }): React.JSX.Element {
  const { i18n } = useLingui()
  if (value === null) return <Text size="sm" />
  return <Text size="sm">{formatInteger(i18n, value)}</Text>
}

function LastHeardCell({ row, now }: { row: IDictionaryRow; now: number }): React.JSX.Element {
  if (row.hits === null) return <Text size="sm" />
  return <RelativeCell iso={row.lastSeenAt} now={now} />
}

/**
 * JargonDictionary renders the accepted-vocabulary panel: a searchable, counted,
 * paginated table of terms with usage stats plus the add-terms input. Dumb
 * component — it owns only its local search / sort / show-more / input state;
 * the host owns the term data, the add/remove mutations, and the confirm-before-
 * remove step.
 */
export function JargonDictionary({
  terms,
  onAdd,
  onRemove,
  isLoading = false,
  isAdding = false,
  stats,
  meetingsCounted = 0,
}: IProps): React.JSX.Element {
  // 1. STATE
  const [query, setQuery] = useState("")
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE)
  const [input, setInput] = useState("")
  const [sortKey, setSortKey] = useState<TSortKey>("rank")
  const [sortDir, setSortDir] = useState<TSortDir>("asc")
  const [now] = useState<number>(() => Date.now())

  // 2. EXTERNAL HOOKS
  const { i18n } = useLingui()

  // 3. DERIVED STATE
  const rows = useMemo(() => buildRows(terms, stats, meetingsCounted), [terms, stats, meetingsCounted])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => r.term.toLowerCase().includes(q))
  }, [rows, query])

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => compareRows(a, b, sortKey, sortDir)),
    [filtered, sortKey, sortDir],
  )

  const visible = sorted.slice(0, visibleCount)
  const hiddenCount = sorted.length - visible.length

  // 4. CALLBACKS
  const handleSearch = useCallback((value: string): void => {
    setQuery(value)
    setVisibleCount(INITIAL_VISIBLE)
  }, [])

  const handleSort = useCallback(
    (key: TSortKey): void => {
      if (key === sortKey) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
        return
      }
      setSortKey(key)
      setSortDir(defaultSortDir(key))
    },
    [sortKey],
  )

  const handleAdd = useCallback((): void => {
    const parsed = Array.from(
      new Set(
        input
          .split(/[,\n]/)
          .map((t) => t.trim())
          .filter(Boolean),
      ),
    )
    if (parsed.length > 0) {
      onAdd(parsed)
      setInput("")
    }
  }, [input, onAdd])

  const handleShowMore = useCallback((): void => {
    setVisibleCount((c) => c + PAGE)
  }, [])

  // 7. RENDER
  return (
    <Stack gap="sm">
      <Alert variant="light" color="info">
        <Text size="sm">
          <Trans>These terms are sent to your transcription provider as vocabulary hints to improve accuracy.</Trans>
        </Text>
      </Alert>

      <Paper withBorder p="md">
        <Stack gap="sm">
          {terms.length > 0 && (
            <TextInput
              value={query}
              onChange={(e) => handleSearch(e.currentTarget.value)}
              placeholder={i18n._(msg`Search terms`)}
              leftSection={<IconSearch size={15} />}
              size="xs"
              aria-label={i18n._(msg`Search dictionary`)}
            />
          )}

          {isLoading && (
            <Table className={classes.table}>
              <Table.Tbody>
                {SKELETON_WIDTHS.map((w, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Skeleton height={16} width={w} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={32} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={32} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={32} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={64} />
                    </Table.Td>
                    <Table.Td />
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          {!isLoading && terms.length === 0 && (
            <Text size="sm" c="dimmed">
              <Trans>No terms yet.</Trans>
            </Text>
          )}

          {!isLoading && terms.length > 0 && filtered.length === 0 && (
            <Text size="sm" c="dimmed">
              <Trans>No terms match "{query}".</Trans>
            </Text>
          )}

          {!isLoading && visible.length > 0 && (
            <Table.ScrollContainer minWidth={560}>
              <Table highlightOnHover verticalSpacing="xs" className={classes.table}>
                <Table.Thead>
                  <Table.Tr>
                    <SortHeader
                      label={<Trans>Term</Trans>}
                      sortBy="term"
                      active={sortKey === "term"}
                      dir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label={<Trans>Heard</Trans>}
                      sortBy="hits"
                      active={sortKey === "hits"}
                      dir={sortDir}
                      numeric
                      onSort={handleSort}
                    />
                    <SortHeader
                      label={<Trans>Meetings</Trans>}
                      sortBy="meetings"
                      active={sortKey === "meetings"}
                      dir={sortDir}
                      numeric
                      onSort={handleSort}
                    />
                    <SortHeader
                      label={<Trans>Fixed</Trans>}
                      sortBy="corrections"
                      active={sortKey === "corrections"}
                      dir={sortDir}
                      numeric
                      onSort={handleSort}
                    />
                    <SortHeader
                      label={<Trans>Last heard</Trans>}
                      sortBy="lastSeenAt"
                      active={sortKey === "lastSeenAt"}
                      dir={sortDir}
                      onSort={handleSort}
                    />
                    <Table.Th aria-label={i18n._(msg`Remove`)} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {visible.map((r) => (
                    <Table.Tr key={r.term} className={classes.row} data-quiet={r.quiet || undefined}>
                      <Table.Td>
                        <Text size="sm" className={classes.term}>
                          {r.term}
                        </Text>
                      </Table.Td>
                      <Table.Td className={classes.numericTd}>
                        <NumericCell value={r.hits} />
                      </Table.Td>
                      <Table.Td className={classes.numericTd}>
                        <NumericCell value={r.meetings} />
                      </Table.Td>
                      <Table.Td className={classes.numericTd}>
                        <NumericCell value={r.corrections} />
                      </Table.Td>
                      <Table.Td>
                        <LastHeardCell row={r} now={now} />
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          size="sm"
                          variant="transparent"
                          onClick={() => onRemove(r.term)}
                          aria-label={i18n._(msg`Remove ${r.term}`)}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}

          {!isLoading && hiddenCount > 0 && (
            <Group justify="center">
              <Button variant="subtle" size="xs" onClick={handleShowMore}>
                <Trans>Show more ({hiddenCount})</Trans>
              </Button>
            </Group>
          )}

          <Group gap="xs" align="flex-end">
            <TextInput
              placeholder={i18n._(msg`e.g. Lexicanon, BYOK, KPI — comma-separated`)}
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd()
              }}
              className={classes.addInput}
            />
            <Button onClick={handleAdd} loading={isAdding} disabled={!input.trim()}>
              <Trans>Add</Trans>
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  )
}
